import { Icon } from "@iconify/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import debounce from "lodash/debounce";
import useApi from "../../hook/useApi";
import { toast } from "react-toastify";
import Loader from "../Loader/Loader";
import Paggination from "../Paggination";
import SaveTvChannelModal from "../model/SaveTvChannelModal";
import { formatDateFull } from "../../utils/helper";
import { PAGE_SIZE_OPTIONS } from "../../constants/pageSizeOptions";
import SearchableSelect from "../Common/SearchableSelect";

/** Typical YouTube video id length; avoids treating channel paths as ids. */
const YOUTUBE_ID_RE = /^[\w-]{11}$/;

/**
 * YouTube only allows cross-origin iframes for /embed/{id} (and similar).
 * Never return youtube.com root, /watch without a valid v=, or random paths — those set X-Frame-Options and refuse to display.
 */
const toIframeSrc = (url) => {
  if (!url || typeof url !== "string") return "";
  const normalized = /^https?:\/\//i.test(url)
    ? url.trim()
    : `https://${url.trim()}`;
  try {
    const parsed = new URL(normalized);
    const host = parsed.hostname.replace(/^www\./, "");
    const isYouTube =
      host === "youtu.be" ||
      host === "youtube.com" ||
      host === "m.youtube.com" ||
      host === "youtube-nocookie.com";

    if (isYouTube) {
      const path = parsed.pathname || "";

      if (host === "youtu.be") {
        const id = path.replace(/^\//, "").split("/")[0]?.split("?")[0];
        return YOUTUBE_ID_RE.test(id || "")
          ? `https://www.youtube.com/embed/${id}`
          : "";
      }
      if (path.startsWith("/embed/")) {
        const id = path.slice("/embed/".length).split("/")[0]?.split("?")[0];
        return YOUTUBE_ID_RE.test(id || "")
          ? `https://www.youtube.com/embed/${id}`
          : "";
      }
      if (path.startsWith("/shorts/")) {
        const id = path.split("/")[2]?.split("?")[0];
        return YOUTUBE_ID_RE.test(id || "")
          ? `https://www.youtube.com/embed/${id}`
          : "";
      }
      if (path.startsWith("/live/")) {
        const id = path.split("/")[2]?.split("?")[0];
        return YOUTUBE_ID_RE.test(id || "")
          ? `https://www.youtube.com/embed/${id}`
          : "";
      }
      if (path.startsWith("/v/")) {
        const id = path.split("/")[2]?.split("?")[0];
        return YOUTUBE_ID_RE.test(id || "")
          ? `https://www.youtube.com/embed/${id}`
          : "";
      }
      const v = parsed.searchParams.get("v");
      if (v && YOUTUBE_ID_RE.test(v)) {
        return `https://www.youtube.com/embed/${v}`;
      }
      return "";
    }

    return normalized;
  } catch {
    return "";
  }
};

const withAutoplay = (src) => {
  if (!src) return "";
  try {
    const parsed = new URL(src);
    parsed.searchParams.set("autoplay", "1");
    parsed.searchParams.set("mute", "1");
    parsed.searchParams.set("playsinline", "1");
    parsed.searchParams.set("rel", "0");
    return parsed.toString();
  } catch {
    return src;
  }
};

const TvChannelLayer = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState({
    pageNo: 1,
    limitVal: 10,
    search: "",
  });
  const [meta, setMeta] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
  });
  const [viewModal, setViewModal] = useState({
    show: false,
    loading: false,
    iframeSrc: "",
    openUrl: "",
    channelName: "",
  });
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [editChannel, setEditChannel] = useState(null);

  const { AsyncGetApiCall, AsyncDeleteApiCall, AsyncPatchApiCall } = useApi();

  const searchDebounceRef = useRef(
    debounce((value) => {
      setQuery((prev) => ({ ...prev, search: value, pageNo: 1 }));
    }, 500),
  );

  useEffect(() => {
    const debounced = searchDebounceRef.current;
    return () => debounced.cancel();
  }, []);

  const fetchChannels = useCallback(async () => {
    setLoading(true);
    const result = await AsyncGetApiCall("/tvchannel/find-all", {
      search: query.search || "",
      pageNo: query.pageNo,
      limitVal: query.limitVal,
    });

    if (result?.errors) {
      toast.error(result.errors[0]);
      setRows([]);
      setMeta({ total: 0, totalPages: 0, currentPage: 1 });
    } else if (result?.success) {
      setRows(Array.isArray(result.data) ? result.data : []);
      setMeta({
        total: result.total ?? 0,
        totalPages: result.totalPages ?? 0,
        currentPage: result.currentPage ?? query.pageNo,
      });
    } else {
      setRows([]);
      setMeta({ total: 0, totalPages: 0, currentPage: 1 });
    }
    setLoading(false);
  }, [AsyncGetApiCall, query.limitVal, query.pageNo, query.search]);

  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  const handleSearchChange = (e) => {
    searchDebounceRef.current(e.target.value);
  };

  const normalizeUrl = (url) => {
    if (!url) return "#";
    return /^https?:\/\//i.test(url) ? url : `https://${url}`;
  };

  const closeViewModal = () => {
    setViewModal({
      show: false,
      loading: false,
      iframeSrc: "",
      openUrl: "",
      channelName: "",
    });
  };

  const closeSaveModal = () => {
    setShowSaveModal(false);
    setEditChannel(null);
  };

  const handleViewChannel = async (channel) => {
    const id = channel?._id;
    if (!id) return;
    setViewModal({
      show: true,
      loading: true,
      iframeSrc: "",
      openUrl: "",
      channelName: channel.tvChannelName || "Channel",
    });
    const rawUrl = channel?.tvChannelUrl ?? "";
    const iframeSrc = rawUrl ? withAutoplay(toIframeSrc(rawUrl)) : "";
    const openUrl = rawUrl ? normalizeUrl(rawUrl) : "";
    setViewModal((prev) => ({
      ...prev,
      loading: false,
      iframeSrc,
      openUrl,
    }));
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this TV channel? This cannot be undone.")) {
      return;
    }
    setLoading(true);
    const result = await AsyncDeleteApiCall(`/tvchannel/delete/${id}`);
    if (result?.errors) {
      toast.error(result.errors[0]);
    } else if (result?.success) {
      toast.success(result.message || "TV channel deleted");
      fetchChannels();
    }
    setLoading(false);
  };

  const handleStatusChange = async (id, isActive) => {
    if (!window.confirm("Are you sure you want to update the status?")) {
      return;
    }
    setLoading(true);
    const payload = {
      isActive: isActive ? false : true,
    };
    const result = await AsyncPatchApiCall(
      `/tvchannel/update/${id}`,
      payload,
    );
    if (result?.errors) {
      toast.error(result?.errors?.[0] || "TV channel status update failed");
    } else if (result?.success) {
      toast.success(result?.message || "TV channel status updated");
      fetchChannels();
    } else {
      toast.error(result?.errors?.[0] || "TV channel status update failed");
    }
    setLoading(false);
  };

  return (
    <>
      {loading && <Loader />}
      <div className="card h-100 p-0 radius-12">
        <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
          <div className="d-flex align-items-center justify-content-between gap-3 w-100">
            <div className="d-flex align-items-center flex-wrap gap-3">
              <span className="text-md fw-medium text-secondary-light mb-0">
                Show
              </span>
              <div style={{ minWidth: "92px" }}>
                <SearchableSelect
                  placeholder="Show"
                  value={String(query.limitVal)}
                  showSearch={false}
                  onChange={(e) => {
                    const value = parseInt(e.target.value, 10);
                    setQuery((prev) => ({
                      ...prev,
                      limitVal: value,
                      pageNo: 1,
                    }));
                  }}
                  options={PAGE_SIZE_OPTIONS.map((n) => ({
                    label: String(n),
                    value: String(n),
                  }))}
                />
              </div>
              <form
                className="navbar-search"
                onSubmit={(e) => e.preventDefault()}
              >
                <input
                  type="text"
                  className="bg-base h-40-px w-auto"
                  placeholder="Search channels"
                  onChange={handleSearchChange}
                  name="search"
                />
                <Icon icon="ion:search-outline" className="icon" />
              </form>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-primary-600 radius-8 px-17 py-9 d-flex align-items-center gap-2"
                onClick={() => {
                  setEditChannel(null);
                  setShowSaveModal(true);
                }}
                disabled={loading}
              >
                <Icon icon="tabler:plus" className="text-xl" />
                Add TV channel
              </button>
            </div>
          </div>
        </div>

        <div className="card-body p-24">
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>Channel name</th>
                  <th>URL</th>
                  <th>Language</th>
                  <th>Status</th>
                  <th>Created at</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length > 0 ? (
                  rows.map((ch, index) => (
                    <tr key={ch._id}>
                      <td>{(query.pageNo - 1) * query.limitVal + index + 1}</td>
                      <td className="fw-medium">{ch.tvChannelName}</td>
                      <td>
                        <span
                          className="text-secondary-light text-truncate d-inline-block"
                          style={{ maxWidth: "240px" }}
                          title={ch.tvChannelUrl}
                        >
                          {ch.tvChannelUrl || "—"}
                        </span>
                      </td>
                      <td>{ch.language || "—"}</td>
                      <td>
                        <span
                          className={`badge px-12 py-4 radius-4 fw-medium text-sm ${ch.isActive
                              ? "bg-success-focus text-success-600"
                              : "bg-danger-focus text-danger-600"
                            } cursor-pointer`}
                          onClick={() =>
                            handleStatusChange(ch._id, ch.isActive)
                          }
                        >
                          {ch.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td>{formatDateFull(ch.createdAt)}</td>
                      <td className="text-center">
                        <div className="d-inline-flex align-items-center justify-content-center gap-1 flex-wrap">
                          <button
                            type="button"
                            title="View"
                            className="bg-info-focus text-info-600 bg-hover-info-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            onClick={() => handleViewChannel(ch)}
                          >
                            <Icon
                              icon="majesticons:eye-line"
                              className="icon"
                            />
                          </button>
                          <button
                            type="button"
                            title="Edit"
                            className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            onClick={() => {
                              setEditChannel(ch);
                              setShowSaveModal(true);
                            }}
                          >
                            <Icon icon="lucide:edit" className="menu-icon" />
                          </button>
                          <button
                            type="button"
                            title="Delete"
                            className="bg-danger-focus text-danger-600 bg-hover-danger-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle border-0"
                            onClick={() => handleDelete(ch._id)}
                          >
                            <Icon
                              icon="mdi:delete-outline"
                              className="menu-icon"
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center py-4">
                      No TV channels found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Paggination
            total={meta.total}
            totalPages={meta.totalPages}
            currentPage={meta.currentPage}
            setPageNo={(page) =>
              setQuery((prev) => ({ ...prev, pageNo: page }))
            }
            startFrom={
              meta.total === 0 ? 0 : (meta.currentPage - 1) * query.limitVal + 1
            }
            endTo={
              meta.total === 0
                ? 0
                : Math.min(meta.currentPage * query.limitVal, meta.total)
            }
          />
        </div>
      </div>

      <SaveTvChannelModal
        show={showSaveModal}
        handleClose={closeSaveModal}
        editData={editChannel}
        onSuccess={fetchChannels}
      />

      <Modal show={viewModal.show} onHide={closeViewModal} size="lg" centered>
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title as="h5" className="mb-0">
            {viewModal.channelName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 bg-dark">
          {viewModal.loading ? (
            <div
              className="d-flex align-items-center justify-content-center py-5"
              style={{ minHeight: "320px" }}
            >
              <div
                className="spinner-border text-primary"
                role="status"
                aria-label="Loading"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : viewModal.iframeSrc ? (
            <iframe
              title={viewModal.channelName}
              src={viewModal.iframeSrc}
              className="w-100 border-0 d-block"
              style={{ minHeight: "420px", aspectRatio: "16/9" }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <div className="text-center text-light py-5 px-4">
              <p className="mb-3">
                This link cannot be shown inside the admin panel. YouTube only
                allows embedding for direct video URLs (for example{" "}
                <code className="text-secondary">watch?v=…</code>,{" "}
                <code className="text-secondary">shorts/…</code>, or{" "}
                <code className="text-secondary">youtu.be/…</code>).
              </p>
              {viewModal.openUrl && viewModal.openUrl !== "#" ? (
                <a
                  href={viewModal.openUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  Open in new tab
                </a>
              ) : (
                <span className="text-secondary">No URL available.</span>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default TvChannelLayer;
